/**
 * BriefIdentityBar — sticky 64px header for the BriefWorkspace.
 *
 * Shows: back button, persona avatar, inline-editable title, lifecycle
 * + health badges, optional strategic-cluster chip, action bar
 * (Generate Ad Pack, Activate, Share dropdown, overflow menu).
 *
 * The HealthBadge always renders "Unscored" in Phase 0 — wired to the
 * nightly rollup in Phase 2.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Archive,
  BarChart2,
  Check,
  Copy,
  ExternalLink,
  GitBranch,
  Loader2,
  MoreHorizontal,
  Share2,
  Sparkles,
  User,
} from 'lucide-react';
import { useGenerationStore } from '@/stores/generation';
import {
  briefShareApi,
  type GenerationBrief,
  type BriefSharePermissions,
  type BriefShareTokenResponse,
} from '@/services/api';
import { WORKSPACE_LAYOUT } from './constants';

interface BriefIdentityBarProps {
  brief: GenerationBrief | null;
  onBack: () => void;
  onNavigateToLens?: (lens: string) => void;
}

export function BriefIdentityBar({ brief, onBack, onNavigateToLens }: BriefIdentityBarProps) {
  const navigate = useNavigate();
  const { activateBrief, archiveBrief, versionBrief, briefActionLoading } =
    useGenerationStore();
  const [shareOpen, setShareOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [scoring, setScoring] = useState(false);

  if (!brief) return <BriefIdentityBarSkeleton />;

  const isActionLoading = briefActionLoading === brief.brief_id;
  const strategicClusterId = (brief as { strategic_cluster_id?: string }).strategic_cluster_id;

  const handleVersion = async () => {
    setOverflowOpen(false);
    const next = await versionBrief(brief.brief_id);
    if (next) navigate(`/creative/briefs/${next.brief_id}`);
  };

  return (
    <div
      className="sticky top-0 z-20 bg-white border-b border-slate-200 px-6 flex items-center gap-4"
      style={{ height: WORKSPACE_LAYOUT.IDENTITY_BAR_HEIGHT }}
    >
      <button
        onClick={onBack}
        className="text-slate-400 hover:text-teal-500 transition-colors"
        title="Back to briefs"
      >
        <ArrowLeft size={16} />
      </button>

      <div className="flex items-center gap-3 flex-1 min-w-0">
        <PersonaAvatarChip
          personaId={brief.persona_id}
          clientUid={brief.client_uid}
          audienceName={(brief.audience as Record<string, unknown> | undefined)?.persona_name as string | undefined}
        />

        <div className="min-w-0 flex flex-col">
          <h1 className="text-sm font-bold text-slate-900 truncate" title={brief.name}>
            {brief.name}
          </h1>
          <span className="font-mono text-[10px] text-slate-400 truncate">
            {brief.brief_id}
          </span>
        </div>

        <LifecycleBadge version={brief.version} status={brief.status} />
        <HealthBadge briefId={brief.brief_id} />

        {strategicClusterId && (
          <button
            onClick={() =>
              navigate(`/creative/strategy?cluster=${strategicClusterId}`)
            }
            className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-100 transition"
            title="Open strategic cluster"
          >
            ⊕ cluster
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Score Brief — makes the health badge actionable */}
        <ScoreBriefButton
          briefId={brief.brief_id}
          scoring={scoring}
          onScore={() => {
            setScoring(true);
            window.setTimeout(() => setScoring(false), 2000);
          }}
        />

        {/* Suggest Angles — primary creative AI action */}
        <button
          onClick={() => onNavigateToLens?.('angles')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-purple rounded-lg hover:bg-brand-purple/90 transition-colors"
        >
          <Sparkles size={12} /> Suggest Angles
        </button>

        {brief.status === 'draft' && (
          <button
            onClick={() => activateBrief(brief.brief_id)}
            disabled={isActionLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50"
          >
            {isActionLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Check size={12} />
            )}
            Activate
          </button>
        )}

        <div className="relative">
          <button
            onClick={() => setShareOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Share2 size={12} /> Share
          </button>
          {shareOpen && (
            <ShareDropdown
              briefId={brief.brief_id}
              clientUid={brief.client_uid}
              onClose={() => setShareOpen(false)}
            />
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setOverflowOpen((v) => !v)}
            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            title="More actions"
          >
            <MoreHorizontal size={14} />
          </button>
          {overflowOpen && (
            <BriefOverflowMenu
              brief={brief}
              isLoading={isActionLoading}
              onVersion={handleVersion}
              onArchive={() => {
                setOverflowOpen(false);
                archiveBrief(brief.brief_id);
              }}
              onClose={() => setOverflowOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function BriefIdentityBarSkeleton() {
  return (
    <div
      className="sticky top-0 z-20 bg-white border-b border-slate-200 px-6 flex items-center gap-3"
      style={{ height: WORKSPACE_LAYOUT.IDENTITY_BAR_HEIGHT }}
    >
      <div className="w-4 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="w-7 h-7 bg-slate-100 rounded-full animate-pulse" />
      <div className="h-4 w-48 bg-slate-100 rounded animate-pulse" />
      <div className="ml-auto h-6 w-32 bg-slate-100 rounded animate-pulse" />
    </div>
  );
}

function PersonaAvatarChip({
  personaId,
  clientUid,
  audienceName,
}: {
  personaId: string;
  clientUid?: string;
  audienceName?: string;
}) {
  const initial = (personaId || '?').replace(/_/g, ' ').slice(0, 1).toUpperCase();
  const hasDistinctAudienceName =
    audienceName && audienceName.toLowerCase() !== personaId.replace(/_/g, ' ').toLowerCase();

  const href =
    personaId && clientUid ? `/personas/${encodeURIComponent(clientUid)}/${encodeURIComponent(personaId)}` : null;

  const inner = (
    <div className="flex items-center gap-2 shrink-0">
      <div
        title={personaId || 'No persona linked'}
        className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-200 to-violet-50 border border-violet-200 flex items-center justify-center text-[10px] font-bold text-violet-700 shrink-0"
      >
        {initial || <User size={12} />}
      </div>
      {personaId && (
        <div className="flex flex-col leading-none">
          {hasDistinctAudienceName && (
            <span className="text-[11px] font-semibold text-slate-700 truncate max-w-[160px]">
              {audienceName}
            </span>
          )}
          <span className="font-mono text-[10px] text-slate-400 truncate max-w-[160px]">
            {personaId}
          </span>
        </div>
      )}
    </div>
  );

  if (href) {
    return (
      <Link
        to={href}
        title="View persona"
        className="hover:opacity-80 transition-opacity"
      >
        {inner}
      </Link>
    );
  }
  return inner;
}

const LIFECYCLE_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  active: 'bg-teal-50 text-teal-700 border-teal-200',
  superseded: 'bg-amber-50 text-amber-700 border-amber-200',
  archived: 'bg-slate-50 text-slate-400 border-slate-200',
};

function LifecycleBadge({ version, status }: { version: number; status: string }) {
  return (
    <span
      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${
        LIFECYCLE_COLORS[status] ?? 'bg-slate-50 text-slate-500 border-slate-200'
      }`}
    >
      v{version} · {status}
    </span>
  );
}

/**
 * HealthBadge — Phase 0 always renders "Unscored".  Wired to nightly
 * brief_health_rollup in Phase 2.
 */
function HealthBadge({ briefId: _briefId }: { briefId: string }) {
  return (
    <span
      className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-300 border-slate-100"
      title="Brief health score — run 'Score Brief' to evaluate"
    >
      · Unscored
    </span>
  );
}

/**
 * ScoreBriefButton — actionable health evaluation trigger.
 * Phase 0: shows a coming-soon tooltip. Phase 2: calls the scoring endpoint.
 */
function ScoreBriefButton({
  briefId: _briefId,
  scoring,
  onScore,
}: {
  briefId: string;
  scoring: boolean;
  onScore: () => void;
}) {
  return (
    <button
      onClick={onScore}
      disabled={scoring}
      title="Evaluate brief quality across clarity, persona fit, and strategic grounding"
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50"
    >
      {scoring ? (
        <Loader2 size={11} className="animate-spin text-brand-purple" />
      ) : (
        <BarChart2 size={11} className="text-slate-400" />
      )}
      {scoring ? 'Scoring…' : 'Score Brief'}
    </button>
  );
}

interface BriefOverflowMenuProps {
  brief: GenerationBrief;
  isLoading: boolean;
  onVersion: () => void;
  onArchive: () => void;
  onClose: () => void;
}

function BriefOverflowMenu({
  brief,
  isLoading,
  onVersion,
  onArchive,
  onClose,
}: BriefOverflowMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onClose]);

  const canArchive = brief.status === 'draft' || brief.status === 'active';

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 z-30 w-52 bg-white rounded-xl border border-slate-200 shadow-lg p-1.5"
    >
      <button
        onClick={onVersion}
        disabled={isLoading}
        className="w-full text-left px-3 py-2 text-xs text-slate-700 rounded hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
      >
        <GitBranch size={12} className="text-slate-400" />
        New version
      </button>
      {canArchive && (
        <button
          onClick={onArchive}
          disabled={isLoading}
          className="w-full text-left px-3 py-2 text-xs text-slate-700 rounded hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
        >
          <Archive size={12} className="text-slate-400" />
          Archive brief
        </button>
      )}
    </div>
  );
}

interface ShareDropdownProps {
  briefId: string;
  clientUid: string;
  onClose: () => void;
}

function ShareDropdown({ briefId, clientUid, onClose }: ShareDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [minted, setMinted] = useState<BriefShareTokenResponse | null>(null);
  const [busyTier, setBusyTier] = useState<BriefSharePermissions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [onClose]);

  const tiers: Array<{ value: BriefSharePermissions; label: string; hint: string }> = [
    { value: 'view', label: 'View only', hint: 'Read-only — chat disabled.' },
    { value: 'comment', label: 'Comment', hint: 'Can chat for Q&A; no edits.' },
    { value: 'suggest', label: 'Suggest edits', hint: 'Section patches; no generations.' },
    { value: 'edit', label: 'Full edit', hint: 'Edits + generations.' },
  ];

  const mint = async (tier: BriefSharePermissions) => {
    setBusyTier(tier);
    setError(null);
    try {
      const res = await briefShareApi.mint(briefId, {
        permissions: tier,
        expires_in_days: 14,
        metadata: { source: 'workspace_share', minted_for: clientUid },
      });
      setMinted(res);
    } catch (e) {
      setError((e as Error).message || 'Failed to create share link');
    } finally {
      setBusyTier(null);
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 z-30 w-72 bg-white rounded-xl border border-slate-200 shadow-lg p-2"
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-2 py-1">
        Create share link
      </div>
      {tiers.map((t) => (
        <button
          key={t.value}
          onClick={() => mint(t.value)}
          disabled={busyTier !== null}
          className="w-full text-left px-3 py-2 rounded hover:bg-slate-50 disabled:opacity-50"
        >
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold text-slate-700">{t.label}</span>
            {busyTier === t.value && (
              <Loader2 size={11} className="animate-spin text-slate-400" />
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">{t.hint}</div>
        </button>
      ))}
      {error && (
        <div className="mt-2 px-2 py-1 text-[10px] text-rose-600 bg-rose-50 border border-rose-200 rounded">
          {error}
        </div>
      )}
      {minted && (
        <div className="mt-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
          <span className="text-[9px] uppercase font-mono tracking-wider text-slate-400">
            {minted.permissions}
          </span>
          <span className="flex-1 font-mono text-[10px] text-slate-600 truncate">
            {minted.share_url}
          </span>
          <button
            onClick={() => handleCopy(minted.share_url)}
            className="text-slate-400 hover:text-brand-teal transition"
            title="Copy"
          >
            {copied ? (
              <Check size={11} className="text-emerald-500" />
            ) : (
              <Copy size={11} />
            )}
          </button>
          <a
            href={minted.share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-brand-teal transition"
            title="Open"
          >
            <ExternalLink size={11} />
          </a>
        </div>
      )}
    </div>
  );
}
